import React, { useState } from "react";
import "./StarRating.css";

interface RatingProps {
  rating?: number;
  onChange?: (rating: number) => void;
  readonly?: boolean;
}

export default function StarRating({
  rating: initialRating = 0,
  onChange: onRatingChange,
  readonly = false,
}: RatingProps) {
  const [rating, setRating] = useState<number>(initialRating);

  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (readonly) return; // Ignore clicks if readonly
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const starWidth = rect.width / 5;
    const newRating = Math.min(
      5,
      Math.max(0, Math.ceil((clickX / starWidth) * 10) / 10),
    );
    setRating(newRating);
    if (onRatingChange) {
      onRatingChange(newRating);
    }
  };

  const renderStar = (index: number) => {
    const fractionalValue = rating - index;

    if (fractionalValue >= 1) {
      // Full star
      return (
        <svg
          key={index}
          className="star full"
          viewBox="0 0 24 24"
          width="36"
          height="36"
        >
          {" "}
          <polygon
            points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"
            fill="yellow"
          />
        </svg>
      );
    } else if (fractionalValue > 0) {
      // Partial star
      return (
        <svg
          key={index}
          className="star partial"
          viewBox="0 0 24 24"
          width="36"
          height="36"
        >
          {" "}
          <defs>
            <linearGradient
              id={`gradient-${index}`}
              x1="0%"
              y1="0%"
              x2="100%"
              y2="0%"
            >
              <stop offset={`${fractionalValue * 100}%`} stopColor="yellow" />
              <stop offset={`${fractionalValue * 100}%`} stopColor="gray" />
            </linearGradient>
          </defs>
          <polygon
            points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"
            fill={`url(#gradient-${index})`}
          />
        </svg>
      );
    } else {
      // Hollow star
      return (
        <svg
          key={index}
          className="star hollow"
          viewBox="0 0 24 24"
          width="36"
          height="36"
        >
          {" "}
          {/* Doubled size */}
          <polygon
            points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"
            fill="gray"
          />
        </svg>
      );
    }
  };

  return (
    <div className="rating-full">
      <div className="rating-container" onClick={handleClick}>
        {[0, 1, 2, 3, 4].map(renderStar)}
      </div>
      <div className="rating-num">{rating.toFixed(1)}</div>
    </div>
  );
}
